import type { BaseMetaHeadProps } from './types';
import type { ReactElement } from 'react';

import Head from 'next/head';
import { memo } from 'react';

import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { getAndroidPlayPackageId } from '~/utilities/constants/androidAppBanner';

const BaseMetaHeadComponent = ({
  title,
  description,
  robots,
  canonicalUrl,
  metaTags,
  iosDeepLink,
  universalLinksRequireGStar = false,
  alwaysRenderSiteName,
  showTwitterCard = true,
  twitterSiteContent,
  children,
  ogImageChildren,
  trailingChildren,
}: BaseMetaHeadProps): ReactElement => {
  const { language, country, isGStar, siteType } = useAppContext();
  const {
    configuration: { siteNameDisplay, appStoreId, appStoreName, enableUniversalLinks },
  } = useStaticContext();
  const androidPackageId = getAndroidPlayPackageId(siteType);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description || ''} key="meta-description" />
      <meta name="robots" content={robots || 'index,follow'} key="meta-robots" />

      {metaTags?.map(tag => {
        const attributes: { [k: string]: string } = {};

        tag.attributes?.forEach(attr => {
          if (attr.name && attr.value) {
            attributes[attr.name] = attr.value;
          }
        });

        return (
          <meta
            key={`meta-${tag.name}`}
            name={tag.name}
            property={tag.property}
            content={tag.content}
            {...attributes}
          />
        );
      })}

      {children}
      {ogImageChildren}

      <meta property="og:locale" content={`${language.toLowerCase()}-${country.toUpperCase()}`} />
      {(alwaysRenderSiteName || siteNameDisplay) && (
        <meta property="og:site_name" content={siteNameDisplay || ''} />
      )}

      {isGStar && (
        <>
          {showTwitterCard && (
            <meta name="twitter:card" content="summary" key="meta-twitter:card" />
          )}
          <meta
            name="twitter:site"
            content={twitterSiteContent || `@GstarRAW_${country.toUpperCase()}`}
            key="meta-twitter:site"
          />
        </>
      )}

      {enableUniversalLinks &&
        iosDeepLink !== undefined &&
        (!universalLinksRequireGStar || isGStar) && (
          <>
            <meta
              property="al:ios:app_name"
              content={appStoreName || ''}
              key="meta-al:ios:app_name"
            />
            <meta
              property="al:ios:app_store_id"
              content={appStoreId || ''}
              key="meta-al:ios:app_store_id"
            />
            <meta property="al:ios:url" content={iosDeepLink} key="meta-al:ios:url" />
            {androidPackageId && (
              <meta
                property="al:android:package"
                content={androidPackageId}
                key="meta-al:android:package"
              />
            )}
            {androidPackageId && (
              <meta property="al:android:url" content={iosDeepLink} key="meta-al:android:url" />
            )}
          </>
        )}

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {trailingChildren}
    </Head>
  );
};

export const BaseMetaHead = memo(BaseMetaHeadComponent);
